import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import ThemeToggle from "@/theme/ThemeToggle";
import AppLauncher from "@/components/AppLauncher";
import Logo from "@/components/Logo";
import Icon from "@/components/Icon";
import UserMenu from "@/components/UserMenu";

/**
 * Page d'accueil (après login) : deux entrées — séries d'exercices ou environnement
 * de développement libre. Esprit « hub » à la basthon, mais avec les icônes SVG du
 * design system (pas d'emoji).
 */
export default function Landing() {
  const { user } = useAuth();

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <Link to="/" style={styles.logo} title="Accueil Python Learning">
          <Logo size={26} />
          Python Learning
        </Link>
        <div style={styles.headerRight}>
          <ThemeToggle />
          <AppLauncher />
          <UserMenu />
        </div>
      </header>

      <main style={styles.main}>
        <p style={styles.kicker}>Bienvenue{user?.username ? `, ${user.username}` : ""}</p>
        <h2 style={styles.title}>Que veux-tu faire&nbsp;?</h2>

        <div style={styles.cards}>
          <Link to="/exercises" className="card" style={styles.card}>
            <span style={{ ...styles.cardIcon, color: "var(--blue)", background: "var(--badge-bg)" }}>
              <Icon name="checklist" size={40} />
            </span>
            <h3 style={styles.cardTitle}>Mes cours</h3>
            <p style={styles.cardDesc}>
              Progresse étape par étape : consignes, validation automatique, indices.
            </p>
          </Link>

          <Link to="/playground" className="card" style={styles.card}>
            <span style={{ ...styles.cardIcon, color: "var(--green)", background: "var(--badge-bg)" }}>
              <Icon name="terminal" size={40} />
            </span>
            <h3 style={styles.cardTitle}>Environnement de développement</h3>
            <p style={styles.cardDesc}>
              Un éditeur et une console libres pour écrire et tester ton propre code.
            </p>
          </Link>

          {/* Réservé aux enseignants : accès direct à l'espace de gestion. */}
          {user?.is_instructor && (
            <Link to="/instructor" className="card" style={styles.card}>
              <span style={{ ...styles.cardIcon, color: "var(--orange)", background: "var(--badge-bg)" }}>
                <Icon name="panel" size={40} />
              </span>
              <h3 style={styles.cardTitle}>Espace enseignant</h3>
              <p style={styles.cardDesc}>
                Crée et gère tes cours, séries et exercices, et suis tes élèves.
              </p>
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { position: "relative", minHeight: "100vh", background: "var(--bg-base)", color: "var(--text)" },
  // Header sorti du flux : le <main> se centre alors sur toute la hauteur de la
  // page (et non sur l'espace résiduel sous l'en-tête), ce qui remonte le bloc.
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 32px",
    background: "var(--bg-bar)",
    borderBottom: "1px solid var(--border-soft)",
  },
  logo: { margin: 0, color: "var(--blue)", fontSize: 22, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  main: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // paddingBottom > paddingTop → le contenu, centré sur 100vh, est remonté
    // légèrement au-dessus du centre exact (rendu plus « ancré »).
    padding: "96px 24px 168px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  kicker: { margin: 0, color: "var(--text-muted)", fontSize: 14, textTransform: "uppercase", letterSpacing: 1 },
  title: { margin: "6px 0 36px", color: "var(--text)", fontSize: 30, fontWeight: 700 },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 320px))",
    gap: 24,
    justifyContent: "center",
    width: "100%",
    maxWidth: 1088, // tient 3 cartes (enseignant) ; 2 restent centrées (élève)
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    // Fond plus clair que --bg-panel : remonte le contraste de la description
    // (--text-muted) au-dessus de AA tout en gardant la distinction avec le titre.
    background: "var(--bg-bar)",
    border: "1px solid var(--border-soft)",
    borderRadius: "var(--radius-lg)",
    padding: "32px 28px",
    textDecoration: "none",
    color: "var(--text)",
    transition: "transform 0.15s, background 0.15s",
  },
  cardIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 80,
    height: 80,
    borderRadius: "var(--radius-lg)",
  },
  cardTitle: { margin: 0, fontSize: 17, color: "var(--text)" },
  cardDesc: { margin: 0, fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.5 },
};
